<!-- eslint-disable vue/require-component-is -->
<template>
  <div>
    <b-dropdown
      id="transition-status"
      toggle-tag="div"
      toggle-class="p-0 bg-transparent"
      menu-class="mc-transition-status__menu"
      v-if="!isCreating && availableStatuses.length > 0"
      :disabled="disabled"
    >
      <template #button-content>
        <McButton
          :variant="variant"
          :text="$t('global.action.changeStatus')"
          :icon="'icon-outline_chevron-down'"
          icon-position="right"
          :disabled="disabled"
        />
      </template>

      <template v-for="(status, idx) in availableStatuses">
        <div
          :key="`item-${idx}`"
          :id="`status-item-${_uid}-${status.id}`"
          class="dropdown-item-wrapper"
          :class="{ disabled: status.disabled }"
          @click="handleItemWrapperClick(status, $event)"
        >
          <b-dropdown-item
            @click="handleShowModal(status)"
            :disabled="status.disabled"
          >
            <span :class="{ 'text-red': status.isRedText }">
              {{ status.label ? $t(status.label) : status.name }}
            </span>
          </b-dropdown-item>
        </div>

        <b-tooltip
          v-if="status.tooltip"
          :key="`tooltip-${idx}`"
          :target="`status-item-${_uid}-${status.id}`"
          :triggers="status.disabled ? 'hover click' : 'hover'"
          placement="right"
          custom-class="formulate-input-tooltip"
          boundary="viewport"
        >
          {{ status.tooltip }}
        </b-tooltip>
      </template>

      <component
        ref="mc-modal"
        :is="dynamicComponent"
        :entityData="entityData"
        :alertTransition="alertTransition"
        :newStatusTransition="newStatusTransition"
        :getStatusLabelFn="getStatusLabelFn"
        @transition="handleTransition"
      />
    </b-dropdown>
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import DefaultTransitionModal from './DefaultTransitionModal'

export default {
  components: {
    McButton,
  },
  props: {
    isCreating: {
      type: Boolean,
      required: true,
    },
    entityData: {
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    statusTransitions: {
      type: Object,
    },
    alertTransition: {
      type: Object,
      default: () => ({}),
    },
    getStatusLabelFn: {
      type: Function,
      default: null,
    },
    variant: {
      type: String,
      default: 'primary',
    }
  },
  emits: ['transition'],
  data() {
    return {
      newStatusTransition: null,
      dynamicComponent: DefaultTransitionModal,
      activeDisabledTooltipTarget: null,
    }
  },
  computed: {
    availableStatuses() {
      const statusName = this.entityData?.status?.name
      if (!statusName) return []

      const transitions = this.statusTransitions?.[statusName]
      if (!transitions || !Array.isArray(transitions)) return []

      return transitions
        .map((t) => ({
          ...t.status,
          isRedText: t.isRedText || false,
          tooltip: t.tooltip || null,
          disabled: t.disabled || false,
        }))
        .filter(Boolean)
    },
  },
  methods: {
    handleItemWrapperClick(status, event) {
      if (!status.disabled) return

      event.preventDefault()
      event.stopPropagation()

      const currentTarget = `status-item-${this._uid}-${status.id}`

      if (
        this.activeDisabledTooltipTarget &&
        this.activeDisabledTooltipTarget !== currentTarget
      ) {
        this.$root.$emit(
          'bv::hide::tooltip',
          this.activeDisabledTooltipTarget
        )
      }

      this.$root.$emit('bv::show::tooltip', currentTarget)
      this.activeDisabledTooltipTarget = currentTarget
    },
    handleTransition(data) {
      this.$emit('transition', data)
      this.$refs['mc-modal'].hide()
      this.newStatusTransition = null
    },
    async handleShowModal(status) {
      const transitions = this.statusTransitions[this.entityData.status.name]
      const newStatusTransition = transitions.find(
        (transition) => transition.status.name === status.name
      )

      this.newStatusTransition = newStatusTransition

      if (newStatusTransition?.customModalTransition) {
        this.dynamicComponent = newStatusTransition.customModalTransition
      } else {
        this.dynamicComponent = DefaultTransitionModal
      }

      await this.$nextTick()

      this.$refs['mc-modal'].show()
    },
  },
}
</script>

<style scoped lang="scss">
:deep(#transition-status) {
  .dropdown-toggle {
    border: none;
  }
  .dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    white-space: normal;
  }

  .dropdown-item-wrapper {
    &.disabled {
      cursor: not-allowed;

      .dropdown-item {
        pointer-events: none !important;
        cursor: not-allowed;
      }
    }
  }

  .mc-transition-status__menu {
    max-width: min(calc(100vw - 32px), 280px);
  }
}
</style>
